/* Sniggles cart — drawer controller + /cart page enhancer. Vanilla JS, no
   framework. Honors the PDP integration contract:
     - exposes window.SnigglesCart = { open, close, refresh, isOpen }
     - listens for document 'sniggles:cart:added' -> open (busy) -> refresh()
     - header bag icon [data-sn-cart-open] click opens the drawer (href="/cart"
       stays as the no-JS fallback)
   Cart state lives in Shopify's AJAX Cart API (/cart/change.js, /cart/update.js).
   The drawer + page re-render through the Section Rendering API, swapping the
   inner HTML of [data-sn-cart-render] so quantities / subtotal / progress stay
   correct without a full reload. Service lines (_service_of / _parent_line) are
   removed together with their parent line.

   Oct 18 (FQ cart fixes, doc 65):
   - Section re-renders are GET requests with NO JSON Accept header. On a
     product URL, `Accept: application/json` makes Shopify answer with the
     product JSON and ignore ?sections= — which is why the bag, count and
     subtotal never updated after Add to bag on product pages. Never send that
     header on a section GET again.
   - The header badge AND its spoken label (aria-label) are rewritten from the
     freshly rendered section's data-cart-count / data-bag-label after EVERY
     change (add, qty, remove, empty), so VoiceOver reads the real count.
   - Added-to-bag shows a visible note + highlights the new line, and is
     announced through a polite live region outside the aria-hidden drawer.
   - Focus survives re-renders (close button / the same line's control).
   - Out-of-order responses are dropped; bfcache restores trigger a refresh. */
(function () {
  'use strict';

  var roots = [];      // { el, sectionId, render }
  var drawer = null;   // the root that owns the slide-in panel
  var lastFocus = null;
  var busy = false;
  var hideTimer = null;
  var noteTimer = null;
  var seq = 0;         // request counter
  var appliedSeq = 0;  // newest response applied

  // ---- helpers -----------------------------------------------------------
  function localeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  // Rendered in the context of the current page (the /cart page section only
  // exists on /cart), but WITHOUT a JSON Accept header — see the Oct 18 note.
  function pagePath() {
    return window.location.pathname || localeRoot();
  }

  function sectionUrl(sectionIds) {
    return pagePath() + '?sections=' + sectionIds.split(',').map(encodeURIComponent).join(',');
  }

  function allSectionIds() {
    return roots.map(function (r) { return r.sectionId; }).join(',');
  }

  function statusEl() {
    return document.querySelector('[data-sn-cart-status]');
  }

  function announce(text) {
    var el = statusEl();
    if (!el || !text) return;
    el.textContent = '';
    // A fresh text node after a tick is what makes VoiceOver re-read it.
    window.setTimeout(function () { el.textContent = text; }, 60);
  }

  function updateCounts(count, label) {
    var n = parseInt(count, 10);
    if (isNaN(n) || n < 0) n = 0;
    document.querySelectorAll('.sn-cart-count').forEach(function (el) {
      el.textContent = n > 0 ? String(n) : '';
    });
    document.querySelectorAll('[data-sn-cart-open]').forEach(function (el) {
      el.setAttribute('data-cart-count', String(n));
      if (label) el.setAttribute('aria-label', label);
    });
  }

  // Remember what had focus inside a render region so it can be restored
  // after the region's HTML is replaced.
  function focusKey(root) {
    var a = document.activeElement;
    if (!a || !root.render || !root.render.contains(a)) return null;
    if (a.closest('[data-sn-cart-close]')) return { sel: '[data-sn-cart-close]' };
    var line = a.closest('[data-sn-line]');
    if (line) {
      var ctl = ['data-sn-qty-inc', 'data-sn-qty-dec', 'data-sn-remove'].filter(function (attr) {
        return a.closest('[' + attr + ']');
      })[0];
      return { line: line.getAttribute('data-line-key'), ctl: ctl || null };
    }
    return { sel: null };
  }

  function restoreFocus(root, key) {
    if (!key) return;
    var target = null;
    if (key.sel) target = root.render.querySelector(key.sel);
    if (!target && key.line) {
      var line = root.render.querySelector('[data-sn-line][data-line-key="' + key.line + '"]');
      if (line && key.ctl) target = line.querySelector('[' + key.ctl + ']:not([disabled])');
      if (!target && line) target = line.querySelector('button:not([disabled])');
    }
    if (!target) {
      target = root.render.querySelector('[data-sn-cart-close]') ||
        (root === drawer ? drawer.el.querySelector('[data-sn-cart-panel]') : null);
    }
    if (target && typeof target.focus === 'function') target.focus();
  }

  // Swap the rendered region of a root from a full section HTML string.
  function applyHTML(root, html) {
    if (!root || !html || !root.render) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-sn-cart-render]');
    if (!fresh) return false;
    var fk = focusKey(root);
    root.render.innerHTML = fresh.innerHTML;
    ['data-cart-count', 'data-bag-label'].forEach(function (attr) {
      if (fresh.getAttribute(attr) != null) root.render.setAttribute(attr, fresh.getAttribute(attr));
    });
    if (fresh.getAttribute('data-cart-count') != null) {
      updateCounts(fresh.getAttribute('data-cart-count'), fresh.getAttribute('data-bag-label'));
    }
    restoreFocus(root, fk);
    return true;
  }

  function fetchSections() {
    var ids = allSectionIds();
    if (!ids) return Promise.resolve(null);
    return fetch(sectionUrl(ids), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) { if (!res.ok) throw new Error('sections ' + res.status); return res.json(); });
  }

  function applySections(sections, mySeq) {
    if (mySeq < appliedSeq) return; // a newer response already landed
    appliedSeq = mySeq;
    roots.forEach(function (root) {
      if (sections && sections[root.sectionId]) applyHTML(root, sections[root.sectionId]);
    });
  }

  function refreshAll() {
    var mySeq = ++seq;
    return fetchSections()
      .then(function (sections) { applySections(sections || {}, mySeq); })
      .catch(function () { /* leave current DOM in place on failure */ });
  }

  function setBusy(state) {
    roots.forEach(function (r) { r.el.classList.toggle('is-busy', state); });
  }

  function mutate(url, body) {
    if (busy) return Promise.resolve();
    busy = true;
    setBusy(true);
    var mySeq = ++seq;
    body.sections = allSectionIds();
    body.sections_url = pagePath();
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var sections = (data && data.sections) || {};
        var missing = roots.some(function (r) { return !sections[r.sectionId]; });
        if (!missing) { applySections(sections, mySeq); return; }
        return fetchSections().then(function (s) { applySections(s || {}, mySeq); });
      })
      .catch(function () { return refreshAll(); })
      .then(function () {
        var r = drawer || roots[0];
        var label = r && r.render && r.render.getAttribute('data-bag-label');
        announce(label);
      })
      .finally(function () {
        busy = false;
        setBusy(false);
      });
  }

  // ---- cart operations ---------------------------------------------------
  function changeQty(key, quantity) {
    return mutate('/cart/change.js', { id: key, quantity: quantity });
  }

  // Remove a line; if it is a parent (not a service) also remove its child
  // service lines (whose data-parent === this line's variant id).
  function removeLine(root, lineEl) {
    var updates = {};
    var key = lineEl.getAttribute('data-line-key');
    updates[key] = 0;
    if (!lineEl.hasAttribute('data-service')) {
      var vid = lineEl.getAttribute('data-variant-id');
      if (vid) {
        root.el.querySelectorAll('[data-sn-line][data-parent]').forEach(function (child) {
          if (child.getAttribute('data-parent') === vid) {
            var ck = child.getAttribute('data-line-key');
            if (ck) updates[ck] = 0;
          }
        });
      }
    }
    return mutate('/cart/update.js', { updates: updates });
  }

  // ---- added-to-bag confirmation -------------------------------------------
  function showAdded(items) {
    if (!drawer || !drawer.render) return;
    var ids = (items || []).map(function (it) { return String(it.variant_id || it.id || ''); });
    var firstLine = null;
    drawer.render.querySelectorAll('[data-sn-line]').forEach(function (line) {
      var hit = ids.indexOf(line.getAttribute('data-variant-id')) !== -1 && !line.hasAttribute('data-service');
      line.classList.toggle('is-new', hit);
      if (hit && !firstLine) firstLine = line;
    });
    var status = statusEl();
    var text = (status && status.getAttribute('data-added-label')) || 'Added to your bag';
    var head = drawer.render.querySelector('.sn-cart__head');
    if (head && !drawer.render.querySelector('[data-sn-cart-added]')) {
      var note = document.createElement('p');
      note.className = 'sn-cart__added';
      note.setAttribute('data-sn-cart-added', '');
      note.setAttribute('aria-hidden', 'true'); // spoken via the live region instead
      note.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';
      note.appendChild(document.createTextNode(' ' + text));
      head.insertAdjacentElement('afterend', note);
    }
    if (firstLine && typeof firstLine.scrollIntoView === 'function') {
      firstLine.scrollIntoView({ block: 'nearest' });
    }
    var label = drawer.render.getAttribute('data-bag-label');
    announce(text + (label ? '. ' + label : ''));
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () {
      var n = drawer && drawer.render && drawer.render.querySelector('[data-sn-cart-added]');
      if (n) n.remove();
      if (drawer && drawer.render) {
        drawer.render.querySelectorAll('[data-sn-line].is-new').forEach(function (l) { l.classList.remove('is-new'); });
      }
    }, 6000);
  }

  // ---- drawer open / close ----------------------------------------------
  function focusables(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function trap(e) {
    if (!drawer || e.key !== 'Tab') return;
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    if (!panel) return;
    var list = focusables(panel);
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen()) { close(); return; }
    trap(e);
  }

  function isOpen() {
    return !!(drawer && drawer.el.classList.contains('is-open'));
  }

  function open() {
    if (!drawer) { window.location.href = localeRoot() + 'cart'; return; }
    if (isOpen()) return;
    window.clearTimeout(hideTimer);
    var scrim = drawer.el.querySelector('[data-sn-cart-scrim]');
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    lastFocus = document.activeElement;
    if (scrim) scrim.hidden = false;
    if (panel) panel.hidden = false;
    drawer.el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sn-cart-open');
    requestAnimationFrame(function () {
      drawer.el.classList.add('is-open');
    });
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeydown);
    if (panel) {
      requestAnimationFrame(function () {
        var closeBtn = panel.querySelector('[data-sn-cart-close]');
        (closeBtn || panel).focus();
      });
    }
  }

  function close() {
    if (!drawer || !isOpen()) return;
    var scrim = drawer.el.querySelector('[data-sn-cart-scrim]');
    var panel = drawer.el.querySelector('[data-sn-cart-panel]');
    drawer.el.classList.remove('is-open');
    drawer.el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sn-cart-open');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(function () {
      if (isOpen()) return;
      if (scrim) scrim.hidden = true;
      if (panel) panel.hidden = true;
    }, 400);
    if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) lastFocus.focus();
  }

  function refresh() {
    return refreshAll();
  }

  // ---- event delegation --------------------------------------------------
  // NB: bindRoot is called with the DOM element. The owning root object is
  // resolved from `roots` at CLICK time, so it stays correct across re-renders
  // and a mismatched argument can never throw during collect().
  function bindRoot(el) {
    el.addEventListener('click', function (e) {
      var root = null;
      for (var i = 0; i < roots.length; i++) {
        if (roots[i].el === el) { root = roots[i]; break; }
      }
      if (!root) return;

      var closeEl = e.target.closest('[data-sn-cart-close]');
      if (closeEl && root === drawer) {
        // Let real navigation links (empty-state) proceed; plain buttons close.
        if (closeEl.tagName !== 'A') { e.preventDefault(); }
        close();
        return;
      }
      var scrim = e.target.closest('[data-sn-cart-scrim]');
      if (scrim) { close(); return; }

      var line = e.target.closest('[data-sn-line]');
      if (!line) return;
      var key = line.getAttribute('data-line-key');

      if (e.target.closest('[data-sn-remove]')) {
        e.preventDefault();
        removeLine(root, line);
        return;
      }
      var val = line.querySelector('[data-sn-qty-val]');
      var current = val ? parseInt(val.textContent, 10) || 1 : 1;
      if (e.target.closest('[data-sn-qty-inc]')) {
        e.preventDefault();
        changeQty(key, current + 1);
      } else if (e.target.closest('[data-sn-qty-dec]')) {
        e.preventDefault();
        if (current > 1) changeQty(key, current - 1);
        else removeLine(root, line);
      }
    });
  }

  // ---- boot --------------------------------------------------------------
  function collect() {
    roots = [];
    drawer = null;
    document.querySelectorAll('[data-render-section]').forEach(function (el) {
      if (!el.querySelector('[data-sn-cart-render]')) return; // not ours
      if (el.dataset.snCartBound !== '1') {
        el.dataset.snCartBound = '1';
        bindRoot(el);
      }
      var root = {
        el: el,
        sectionId: el.getAttribute('data-render-section'),
        render: el.querySelector('[data-sn-cart-render]')
      };
      roots.push(root);
      if (el.hasAttribute('data-sn-cart') && el.querySelector('[data-sn-cart-panel]')) {
        drawer = root;
      }
    });
  }

  function boot() {
    collect();

    // Header bag icon(s) -> open drawer (progressive enhancement).
    document.querySelectorAll('[data-sn-cart-open]').forEach(function (el) {
      if (el.dataset.snCartOpenBound === '1') return;
      el.dataset.snCartOpenBound = '1';
      el.addEventListener('click', function (e) {
        if (!drawer) return; // no drawer on this page: follow href to /cart
        e.preventDefault();
        open();
      });
    });

    // Bring the header label in line with what the server rendered for the
    // drawer (covers pages served from cache with an older count).
    var r = drawer || roots[0];
    if (r && r.render) {
      updateCounts(r.render.getAttribute('data-cart-count'), r.render.getAttribute('data-bag-label'));
    }

    // PDP / quick-view add-to-bag contract. Open straight away (dimmed) so the
    // tap gets instant feedback, then swap in the real cart and confirm.
    if (!window.__snCartAddedBound) {
      window.__snCartAddedBound = true;
      document.addEventListener('sniggles:cart:added', function (e) {
        var items = (e && e.detail && e.detail.items) || [];
        setBusy(true);
        open();
        refreshAll().then(function () {
          setBusy(false);
          showAdded(items);
        });
      });

      // Back/forward cache restores a frozen page with an old badge.
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) refreshAll();
      });
    }
  }

  window.SnigglesCart = {
    open: open,
    close: close,
    refresh: refresh,
    isOpen: isOpen
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-collect when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
